/**
 * Light / candlelight theming.
 *
 * The chosen theme is persisted in localStorage; with no choice on record the
 * OS preference decides. An inline script in index.html applies the resolved
 * theme to <html data-theme> before first paint, so the page never flashes the
 * wrong colours — this module keeps that attribute in step afterwards.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'pc-theme';

/** The theme the user explicitly chose, or null if they never have. */
export function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

/** The theme to show now: an explicit choice if there is one, else the OS. */
export function resolveTheme(): Theme {
  return (
    storedTheme() ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  );
}

/**
 * Reflects a theme onto the document — the `data-theme` attribute the CSS keys
 * off, and the browser-chrome colour. Does not persist the choice.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#211e1a' : '#f7f4ee');
}

/** Persists an explicit theme choice, then applies it. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // A private-mode storage failure must not break the toggle.
  }
  applyTheme(theme);
}
